/* 語系核心：純函式，不含 JSX —— 這樣 node --test 可以直接載入它。 */
import { CATALOGS } from "./messages.ts";
import type { Locale, MessageKey } from "./messages.ts";

export type { Locale, MessageKey };
export type Params = Record<string, string | number>;
export type LocaleSetting = "auto" | Locale;

export function detectLocale(setting: LocaleSetting | undefined, navLang?: string): Locale {
  const raw = setting && setting !== "auto"
    ? setting
    : (navLang ?? (typeof navigator !== "undefined" ? navigator.language : "en"));
  const lower = String(raw).toLowerCase();
  if (lower.startsWith("zh")) return "zh-Hant"; // zh-TW／zh-HK／zh-Hant 皆繁中；加簡中＝多一份目錄
  return "en";
}

export function interpolate(template: string, params?: Params): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (whole, key: string) =>
    params[key] === undefined ? whole : String(params[key]));
}

export type Translate = (key: MessageKey, params?: Params) => string;
/** 英文分單複數、中文不分；兩份目錄都必須提供 .one 與 .other。 */
export type TranslatePlural = (base: string, n: number, params?: Params) => string;

export interface I18nValue {
  locale: Locale;
  t: Translate;
  tn: TranslatePlural;
  formatDate: (ms: number) => string;
  formatDateTime: (ms: number) => string;
  formatNumber: (n: number) => string;
}

export function buildI18n(locale: Locale): I18nValue {
  const catalog = CATALOGS[locale];
  const t: Translate = (key, params) => interpolate(catalog[key], params);
  const tn: TranslatePlural = (base, n, params) =>
    interpolate(catalog[(n === 1 ? base + ".one" : base + ".other") as MessageKey], { n, ...params });
  const tag = locale === "en" ? "en-US" : "zh-Hant-TW";
  const dateFmt = new Intl.DateTimeFormat(tag, { year: "numeric", month: "short", day: "numeric" });
  const dateTimeFmt = new Intl.DateTimeFormat(tag, { dateStyle: "short", timeStyle: "short" });
  const numFmt = new Intl.NumberFormat(tag);
  return {
    locale, t, tn,
    formatDate: (ms) => dateFmt.format(new Date(ms)),
    formatDateTime: (ms) => dateTimeFmt.format(new Date(ms)),
    formatNumber: (n) => numFmt.format(n),
  };
}

/** 種子類別若沒被改過名，就跟著介面語言翻譯；改過名就是使用者自己的資料，不動。 */
export function localizedCategoryName(
  cat: { name: string; seedKey?: string }, t: Translate, originalSeedName: (key: string) => string | undefined,
): string {
  if (!cat.seedKey) return cat.name;
  const original = originalSeedName(cat.seedKey);
  if (original === undefined || original !== cat.name) return cat.name;
  return t(("category." + cat.seedKey) as MessageKey);
}
